import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/**
 * Logo Lab — eight upscale directions for the Bidorah "B" mark.
 *
 * All variants now share the SITE palette — the teal gradient card
 * (#2190CC → #1D78AB → #0E3F5C) + white B + amber accents — so we're
 * comparing ONLY the B glyph style (serif, hairline, chevron, faceted,
 * neon, ingot, emblem, carved). Pick the shape you like and I'll ship it.
 */
export default function LogoLab() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="logo-lab-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">
        <p className="overline text-zinc-500">PICK A DIRECTION</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
          The "B." 8 directions — same palette.
        </h1>
        <p className="mt-3 text-zinc-600 max-w-2xl">
          Same teal gradient card + white B + amber accents as the site today. The difference between each swatch is the <em>shape of the B itself</em> — serif, hairline, chevron, faceted, neon, emblem, ingot, carved. Tell me the number and I'll swap it into the mark everywhere.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-10">
          {VARIANTS.map((V, i) => (
            <Swatch key={i} idx={i + 1} {...V} />
          ))}
        </div>

        <section className="mt-16 rounded-2xl border border-zinc-200 bg-zinc-50 p-8 text-center">
          <p className="font-display font-black text-2xl tracking-tighter">
            Ready to lock it in?
          </p>
          <p className="text-sm text-zinc-600 mt-1">
            Tell me the number — I'll ship it to navbar, phone mockup, favicon and footer.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Swatch({ idx, name, tagline, Mark }) {
  return (
    <div
      className="rounded-3xl border border-zinc-200 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr,1.4fr]"
      data-testid={`swatch-${idx}`}
    >
      <div className="p-10 flex items-center justify-center bg-gradient-to-br from-zinc-50 via-white to-zinc-50">
        <Mark size={200} />
      </div>
      <div className="p-8 flex flex-col gap-5">
        <div>
          <p className="text-xs font-bold tracking-widest text-zinc-400">
            OPTION {String(idx).padStart(2, "0")}
          </p>
          <p className="font-display font-black text-3xl tracking-tighter mt-1">
            {name}
          </p>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{tagline}</p>
        </div>

        <div className="flex items-end gap-6 flex-wrap">
          <SizedCell size={20} label="Favicon" Mark={Mark} />
          <SizedCell size={36} label="Nav" Mark={Mark} />
          <SizedCell size={64} label="Hero" Mark={Mark} />
          <SizedCell size={96} label="Feature" Mark={Mark} />
        </div>

        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 text-white px-3 py-1">
            <Mark size={14} /> Bidorah
          </span>
          <span className="text-zinc-500">Used inline with the wordmark.</span>
        </div>
      </div>
    </div>
  );
}

function SizedCell({ size, label, Mark }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="p-3 rounded-xl bg-white border border-zinc-200"
        style={{ minWidth: size + 24, minHeight: size + 24, display: "grid", placeItems: "center" }}
      >
        <Mark size={size} />
      </div>
      <p className="text-[10px] tracking-widest text-zinc-400 font-bold uppercase">
        {label} · {size}px
      </p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   SHARED PALETTE (matches current live BidoraMark)
   ──────────────────────────────────────────────────────────────────────── */

/** Standard teal card background — identical to the site's current mark. */
function TealCard({ uid, shine = true }) {
  return (
    <>
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2190CC" />
          <stop offset="55%" stopColor="#1D78AB" />
          <stop offset="100%" stopColor="#0E3F5C" />
        </linearGradient>
        {shine && (
          <linearGradient id={`${uid}-shine`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.28" />
            <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        )}
      </defs>
      <rect x="0" y="0" width="40" height="40" rx="10" fill={`url(#${uid}-bg)`} />
      {shine && (
        <rect x="0" y="0" width="40" height="22" rx="10" fill={`url(#${uid}-shine)`} />
      )}
    </>
  );
}

const AMBER = "#F59E0B";
const WHITE = "#FFFFFF";
const WHITE_SOFT = "#F2F6FA";

/* ────────────────────────────────────────────────────────────────────────
   VARIANTS — all share the teal card; the B changes shape.
   ──────────────────────────────────────────────────────────────────────── */

const VARIANTS = [
  {
    name: "Vault — Serif with bracketed terminals",
    tagline:
      "Formal serif B with thicker stems, inset cross-bar and tiny bracket serifs — feels like a Swiss watchmaker's seal, still on the Bidorah teal.",
    Mark: MarkVault,
  },
  {
    name: "Apex — Bid-up chevron",
    tagline:
      "The existing rounded B with a double amber up-chevron to its right — a literal 'bid rising' cue baked into the mark.",
    Mark: MarkApex,
  },
  {
    name: "Didone — Editorial hairline",
    tagline:
      "High-contrast Didone-style B with thick verticals and hairline curves. Editorial magazine feel on the Bidorah teal.",
    Mark: MarkDidone,
  },
  {
    name: "Gem — Faceted crystal",
    tagline:
      "Triangulated faceted B that reads like a cut gemstone. Facets catch light differently at each size — modern and premium.",
    Mark: MarkGem,
  },
  {
    name: "Emblem — Double-ring crest",
    tagline:
      "Rounded B enclosed in twin amber rings with three laurel dots — the aesthetic of a century-old auction house.",
    Mark: MarkEmblem,
  },
  {
    name: "Signal — Neon glow",
    tagline:
      "Bright, slightly emissive B with a soft outer glow and pulse dot. Livestream-era cinematic — Gen-Z leaning.",
    Mark: MarkSignal,
  },
  {
    name: "Ingot — Precision bar",
    tagline:
      "Top edge hairline + bottom shadow line give the card weight. Bold geometric B with an amber ticker line — reads like a pressed bar.",
    Mark: MarkIngot,
  },
  {
    name: "Carved — Engraved depth",
    tagline:
      "B feels debossed into the card via a soft inner shadow + outer rim. Museum-wall premium without changing the palette.",
    Mark: MarkCarved,
  },
];

/* ────────────────────────────────────────────────────────────────────────
   MARK 1 — Vault (serif, bracketed terminals)
   ──────────────────────────────────────────────────────────────────────── */
function MarkVault({ size = 40 }) {
  const uid = "v1";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      <path
        d="M11 8 H21.5 C25.2 8 27.8 10.2 27.8 13.5 C27.8 15.8 26.5 17.6 24.2 18.3 C27.4 19 29 21 29 24 C29 27.8 26 30 21.3 30 H11 Z
           M15.2 11.4 V18.2 H20.2 C22.2 18.2 23.4 17 23.4 15 C23.4 13 22.2 11.4 20.2 11.4 Z
           M15.2 21.4 V26.6 H20.8 C23.2 26.6 24.4 25.6 24.4 23.9 C24.4 22.2 23.2 21.4 20.8 21.4 Z"
        fill={WHITE}
      />
      {/* Bracketed serifs */}
      <rect x="9.4" y="8" width="2" height="0.9" fill={WHITE} />
      <rect x="9.4" y="29.1" width="2" height="0.9" fill={WHITE} />
      {/* Amber quote accent */}
      <circle cx="31.5" cy="31.5" r="1.6" fill={AMBER} />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 2 — Apex (B + bid-up chevron)
   ──────────────────────────────────────────────────────────────────────── */
function MarkApex({ size = 40 }) {
  const uid = "v2";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      <path
        d="M10 9 H19.8 C23.4 9 25.7 11 25.7 13.9 C25.7 15.9 24.6 17.4 22.8 18 C25.1 18.5 26.4 20.2 26.4 22.6 C26.4 26 23.9 27.6 20 27.6 H10 Z
           M13.6 12.3 V17.3 H19 C20.9 17.3 21.8 16.3 21.8 14.8 C21.8 13.3 20.9 12.3 19 12.3 Z
           M13.6 20.3 V24.3 H19.3 C21.5 24.3 22.5 23.6 22.5 22.3 C22.5 21 21.5 20.3 19.3 20.3 Z"
        fill={WHITE}
      />
      {/* Double amber up-chevrons — 'bid rising' */}
      <path d="M29 24 L32 20.5 L35 24" stroke={AMBER} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M29 28 L32 24.5 L35 28" stroke={AMBER} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.55" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 3 — Didone (high-contrast editorial B)
   ──────────────────────────────────────────────────────────────────────── */
function MarkDidone({ size = 40 }) {
  const uid = "v3";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      {/* Thick stem */}
      <rect x="10.5" y="8.5" width="3.8" height="23" fill={WHITE} />
      {/* Top bowl hairline */}
      <path d="M14.3 8.5 H20.5 C23.6 8.5 26.2 10.6 26.2 13.7 C26.2 16.6 24.2 18.6 20.5 18.7 H14.3"
            fill="none" stroke={WHITE} strokeWidth="2.5" />
      {/* Bottom bowl */}
      <path d="M14.3 18.9 H21.1 C24.6 18.9 27.3 21.3 27.3 24.9 C27.3 28.5 24.9 31.5 20.5 31.5 H14.3"
            fill="none" stroke={WHITE} strokeWidth="2.5" />
      {/* Bracketed stem serifs */}
      <rect x="9" y="8.5" width="6.8" height="1.1" fill={WHITE} />
      <rect x="9" y="30.4" width="6.8" height="1.1" fill={WHITE} />
      {/* Amber quote accent */}
      <circle cx="30.5" cy="31.5" r="1.4" fill={AMBER} />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 4 — Gem (faceted B)
   ──────────────────────────────────────────────────────────────────────── */
function MarkGem({ size = 40 }) {
  const uid = "v4";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      {/* Faceted B silhouette — three tonal whites for light/shadow */}
      <g>
        {/* Left stem — bright */}
        <polygon points="10,9 14,9 14,31 10,31" fill={WHITE} />
        {/* Upper bowl — mid */}
        <polygon points="14,9 21,9 25,14 21,18 14,18" fill={WHITE_SOFT} opacity="0.88" />
        <polygon points="21,9 25,14 25.5,13 21.5,9" fill={WHITE} opacity="0.95" />
        <polygon points="14,18 21,18 25,14 26,19 21,20 14,19.5" fill={WHITE_SOFT} opacity="0.55" />
        {/* Lower bowl */}
        <polygon points="14,20 22,20 27,25 22,31 14,31" fill={WHITE_SOFT} opacity="0.88" />
        <polygon points="22,20 27,25 26.5,26 22,22" fill={WHITE} opacity="0.9" />
        <polygon points="14,31 22,31 27,25 28,30 22,32 14,32" fill={WHITE_SOFT} opacity="0.55" />
      </g>
      <circle cx="30" cy="11" r="0.9" fill={AMBER} opacity="0.95" />
      <circle cx="32" cy="9.5" r="0.4" fill={WHITE} opacity="0.7" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 5 — Emblem (double-ring crest)
   ──────────────────────────────────────────────────────────────────────── */
function MarkEmblem({ size = 40 }) {
  const uid = "v5";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      <circle cx="20" cy="20" r="15.5" fill="none" stroke={AMBER} strokeWidth="0.8" opacity="0.95" />
      <circle cx="20" cy="20" r="13.5" fill="none" stroke={AMBER} strokeWidth="0.4" opacity="0.7" />
      <path
        d="M13 10 H20.5 C23.4 10 25.5 11.6 25.5 14 C25.5 15.7 24.5 16.9 22.8 17.4 C25.1 17.9 26.3 19.4 26.3 21.5 C26.3 24.5 24 26 20.5 26 H13 Z
           M16 12.8 V16.8 H19.8 C21.4 16.8 22.2 16 22.2 14.8 C22.2 13.6 21.4 12.8 19.8 12.8 Z
           M16 19 V23.2 H20.2 C22 23.2 22.9 22.4 22.9 21.1 C22.9 19.8 22 19 20.2 19 Z"
        fill={WHITE}
      />
      {/* Laurel dots */}
      <circle cx="13" cy="32" r="0.8" fill={AMBER} />
      <circle cx="27" cy="32" r="0.8" fill={AMBER} />
      <circle cx="20" cy="33.5" r="0.8" fill={AMBER} />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 6 — Signal (neon glow)
   ──────────────────────────────────────────────────────────────────────── */
function MarkSignal({ size = 40 }) {
  const uid = "v6";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      <defs>
        <filter id={`${uid}-blur`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.3" />
        </filter>
      </defs>
      {/* Glow layer (soft) */}
      <path
        d="M11 9.5 H20.5 C24 9.5 26.3 11.5 26.3 14.4 C26.3 16.4 25.2 18 23.2 18.6 C25.5 19.2 27 21 27 23.5 C27 27 24.4 28.8 20.5 28.8 H11 Z
           M14.5 12.7 V17.7 H19.5 C21.4 17.7 22.3 16.7 22.3 15.2 C22.3 13.7 21.4 12.7 19.5 12.7 Z
           M14.5 20.7 V25.7 H19.8 C22 25.7 23 24.8 23 23.3 C23 21.8 22 20.7 19.8 20.7 Z"
        fill={WHITE}
        opacity="0.55"
        filter={`url(#${uid}-blur)`}
      />
      {/* Crisp B on top */}
      <path
        d="M11 9.5 H20.5 C24 9.5 26.3 11.5 26.3 14.4 C26.3 16.4 25.2 18 23.2 18.6 C25.5 19.2 27 21 27 23.5 C27 27 24.4 28.8 20.5 28.8 H11 Z
           M14.5 12.7 V17.7 H19.5 C21.4 17.7 22.3 16.7 22.3 15.2 C22.3 13.7 21.4 12.7 19.5 12.7 Z
           M14.5 20.7 V25.7 H19.8 C22 25.7 23 24.8 23 23.3 C23 21.8 22 20.7 19.8 20.7 Z"
        fill={WHITE}
      />
      <circle cx="31.5" cy="31.5" r="2" fill={AMBER}>
        <animate attributeName="opacity" values="1;0.35;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 7 — Ingot (precision bar)
   ──────────────────────────────────────────────────────────────────────── */
function MarkIngot({ size = 40 }) {
  const uid = "v7";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <TealCard uid={uid} />
      {/* Hairlines */}
      <line x1="4" y1="10" x2="36" y2="10" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="0.4" />
      <line x1="4" y1="30" x2="36" y2="30" stroke="#000000" strokeOpacity="0.18" strokeWidth="0.4" />
      {/* Bold geometric B */}
      <path
        d="M11 9.5 H20.5 C23.8 9.5 26 11.4 26 14.1 C26 16 24.9 17.6 23 18.1 C25.4 18.6 26.8 20.3 26.8 22.8 C26.8 26 24.2 27.8 20.3 27.8 H11 Z
           M14.5 12.7 V17.4 H19.6 C21.4 17.4 22.3 16.5 22.3 15 C22.3 13.5 21.4 12.7 19.6 12.7 Z
           M14.5 20.4 V24.6 H20 C22.1 24.6 23.1 23.8 23.1 22.4 C23.1 21 22.1 20.4 20 20.4 Z"
        fill={WHITE}
      />
      {/* Amber ticker line */}
      <rect x="28.5" y="24" width="5" height="1.2" rx="0.6" fill={AMBER} />
      <rect x="28.5" y="26.5" width="3.2" height="1.2" rx="0.6" fill={AMBER} opacity="0.7" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   MARK 8 — Carved (engraved depth)
   ──────────────────────────────────────────────────────────────────────── */
function MarkCarved({ size = 40 }) {
  const uid = "v8";
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2190CC" />
          <stop offset="55%" stopColor="#1D78AB" />
          <stop offset="100%" stopColor="#0E3F5C" />
        </linearGradient>
        <filter id={`${uid}-emboss`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="0.6" />
          <feOffset dx="0" dy="0.6" result="off" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.6" />
          </feComponentTransfer>
        </filter>
      </defs>
      <rect x="0" y="0" width="40" height="40" rx="10" fill={`url(#${uid}-bg)`} />
      {/* Soft inner highlight line along top edge for 'engraved' feel */}
      <rect x="0" y="0" width="40" height="22" rx="10" fill="#FFFFFF" fillOpacity="0.22" />
      {/* Dark inset shadow of the B below the white plate — creates depth */}
      <path
        d="M12.5 10.5 H21 C24 10.5 26 12.5 26 15.2 C26 17.1 25 18.6 23.2 19.2 C25.5 19.8 26.8 21.4 26.8 23.8 C26.8 27 24.3 28.8 20.5 28.8 H12.5 Z"
        fill="#000000"
        fillOpacity="0.25"
      />
      {/* Main engraved B */}
      <path
        d="M12 9.5 H21 C24 9.5 26 11.5 26 14.2 C26 16.1 25 17.6 23.2 18.2 C25.5 18.8 26.8 20.4 26.8 22.8 C26.8 26 24.3 27.8 20.5 27.8 H12 Z
           M15.2 12.7 V17.4 H20 C21.7 17.4 22.5 16.5 22.5 15 C22.5 13.5 21.7 12.7 20 12.7 Z
           M15.2 20.4 V24.6 H20.5 C22.5 24.6 23.4 23.8 23.4 22.4 C23.4 21 22.5 20.4 20.5 20.4 Z"
        fill={WHITE}
      />
      {/* Fine serifs */}
      <rect x="10.6" y="9.5" width="2.4" height="0.7" fill={WHITE_SOFT} />
      <rect x="10.6" y="27.1" width="2.4" height="0.7" fill={WHITE_SOFT} />
      {/* Amber dot */}
      <circle cx="31.5" cy="31.5" r="1.6" fill={AMBER} />
    </svg>
  );
}
